/**
 * The company itself — the handful of facts a formal document has to state.
 *
 * Document: `skidaCrmSettings/company`. Written by admins, read by everybody,
 * like the pipeline and the templates beside it.
 *
 * It exists because two features needed the same four facts and neither should
 * own them: the payment request needs the account money is paid into, and the
 * board minutes need the legal name, the organisation number, the par value and
 * who sits on the board. Typing those into each document is how two documents
 * end up disagreeing about the company that issued them.
 *
 * ## Par value is the one that cannot be derived
 *
 * A Norwegian AS states its share capital in kroner: shares × pålydende. The
 * register knows the share count and the price paid, and **neither of those is
 * the par value** — price is what an investor paid, par is what the share is
 * worth on the register, and the difference is overkurs. Without it the minutes
 * cannot state a capital figure at all, so the generator refuses rather than
 * inventing one.
 */

export interface BoardMember {
  name: string
  /** Styreleder, styremedlem, daglig leder — free text, as the minutes print it. */
  role: string
}

export interface CompanyDetails {
  /** As registered, for the head of a formal document. */
  legalName: string
  /** Organisasjonsnummer, nine digits. Printed as typed. */
  orgNumber: string
  /** Where a payment request asks investors to pay. Editable per send. */
  bankAccount: string
  /** Pålydende per share, in kroner. Null until somebody sets it. */
  parValue: number | null
  /** Who signs the minutes, in the order they should appear. */
  board: BoardMember[]
}

export const EMPTY_COMPANY: CompanyDetails = {
  legalName: '',
  orgNumber: '',
  bankAccount: '',
  parValue: null,
  board: [],
}
